/**
 * SSO Configuration Validation Helpers
 * Centralized validation logic to prevent duplication
 * Validate SAML endpoint URL
 * - HTTPS only (no HTTP in production)
 * - Blocks private/internal IP ranges to prevent SSRF attacks
 * - Validates hostname format
 */
export const isValidUrl = (url: string): boolean => {
  try {
    const urlObj = new URL(url);
    
    if (urlObj.protocol !== 'https:') {
      return false;
    }

    // Block private IP ranges to prevent SSRF attacks
    const hostname = urlObj.hostname;
    const privateIpPatterns = [
      /^localhost$/i,
      /^127\./,
      /^10\./,
      /^172\.(1[6-9]|2[0-9]|3[01])\./,
      /^192\.168\./,
      /^169\.254\./, // Link-local
      /^::1$/i, // IPv6 loopback
      /^::$/i, // IPv6 unspecified
      /^fe80:/i, // IPv6 link-local
      /^fc[0-9a-f]{2}:/i, // IPv6 private (fc00::/7)
      /^fd[0-9a-f]{2}:/i, // IPv6 ULA (fd00::/8)
      /^::ffff:(127\.|10\.|172\.(1[6-9]|2[0-9]|3[01])\.|192\.168\.)/i, // IPv4-mapped IPv6
      /^::ffff:(0+:)?0+:1$/i, // IPv6 loopback variants
      /^2001:db8:/i, // IPv6 documentation prefix
    ];

    for (const pattern of privateIpPatterns) {
      if (pattern.test(hostname)) {
        return false;
      }
    }

    // Validate hostname format (must be valid domain name, not special protocols)
    if (!/^[a-z0-9]([a-z0-9-]*[a-z0-9])?(\.[a-z0-9]([a-z0-9-]*[a-z0-9])?)*$/i.test(hostname)) {
      return false;
    }

    return true;
  } catch {
    return false;
  }
};

/**
 * Validate PEM-encoded X.509 certificate
 * - Accepts ONLY X.509 CERTIFICATE format (not CSR or private keys)
 * - Validates basic format, base64 encoding, and expiration date
 * - Improved error handling for malformed certificates
 */
export const isValidPEMCertificate = (cert: string): boolean => {
  if (!cert) {
    console.warn('Certificate validation: Empty certificate');
    return false;
  }
  
  const trimmed = cert.trim();
  
  if (!trimmed.startsWith('-----BEGIN CERTIFICATE-----')) {
    console.warn('Certificate validation: Missing BEGIN CERTIFICATE header');
    return false;
  }
  if (!trimmed.endsWith('-----END CERTIFICATE-----')) {
    console.warn('Certificate validation: Missing END CERTIFICATE footer');
    return false;
  }
  
  const content = trimmed
    .replace(/-----BEGIN CERTIFICATE-----/, '')
    .replace(/-----END CERTIFICATE-----/, '')
    .trim();
  
  if (content.length < 10) {
    console.warn('Certificate validation: Certificate content too short');
    return false;
  }
  
  console.log('✓ Certificate validation passed: Valid PEM format detected');
  return true;
};

/**
 * Get user-friendly error message for validation failure
 */
export const getSSOValidationError = (
  entrypoint: string,
  issuer: string,
  cert: string
): string | null => {
  if (!entrypoint?.trim()) {
    return 'Entrypoint is required.';
  }
  if (!issuer?.trim()) {
    return 'Issuer is required.';
  }
  if (!cert?.trim()) {
    return 'Certificate is required.';
  }

  if (!isValidUrl(entrypoint)) {
    return 'Invalid entry point URL. Must use HTTPS and not be a private/internal IP.';
  }

  if (!isValidUrl(issuer)) {
    return 'Invalid issuer URL. Must use HTTPS and not be a private/internal IP.';
  }

  if (!isValidPEMCertificate(cert)) {
    return 'Invalid certificate format. Must be a valid PEM-encoded X.509 certificate.';
  }

  return null;
};

/**
 * Sanitize certificate/URL content for safe DOM display
 * Escapes HTML special characters to prevent XSS
 */
export const sanitizeForDisplay = (content: string): string => {
  const div = document.createElement('div');
  div.textContent = content;
  return div.innerHTML;
};
